import { useRef } from "react";
import { twMerge } from "tailwind-merge";
import { Check } from "lucide-react";
import { Type } from "../Type";

export type Props = {
  title: string;
  description?: string;
  checked?: boolean;
  onChange: (checked: boolean) => void;
  isDisabled?: boolean;
  isSelectable?: boolean;
  isRemovable?: boolean;
  className?: string;
  isChecked?: boolean;
  isCheckOnClick?: boolean;
  /** Custom test ID for the chip element */
  testId?: string;
};

export function SelectableChip({
  title,
  isChecked,
  onChange,
  isDisabled = false,
  isSelectable = true,
  className,
  isCheckOnClick = true,
  testId = "selectable-chip",
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (!isDisabled || isCheckOnClick) {
      onChange(!isChecked);
      ref.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled) return;
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault(); // prevent scrolling
      onChange(!isChecked);
    }
  };

  return (
    <div
      ref={ref}
      data-testid={testId}
      tabIndex={isDisabled ? -1 : 0}
      role="checkbox"
      aria-checked={isChecked}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={twMerge(
        "inline-flex items-center rounded-full border px-3 py-1 transition-colors focus:ring-transparent focus:outline-brand-600 focus:ring-2 focus:outline-offset-2 focus:outline-2 hover:shadow-lg cursor-pointer",
        isDisabled &&
          "hover:shadow-none bg-brand-100 text-brand-300 border-brand-300",
        isDisabled && !isCheckOnClick && "cursor-not-allowed",
        isChecked && "border-brand-600 border-2",
        className
      )}
    >
      {isSelectable && (
        <div
          className={twMerge(
            "w-3 transition-all duration-200 ease-in-out",
            isChecked
              ? "opacity-100 translate-x-0 mr-2"
              : "opacity-0 -translate-x-1"
          )}
        >
          <Check size={12} strokeWidth={4} />
        </div>
      )}
      <Type
        variant="h6"
        as="span"
        className={twMerge(
          "transition-all duration-200",
          isSelectable && !isChecked && "mr-2"
        )}
      >
        {title}
      </Type>
    </div>
  );
}

export default SelectableChip;

